import Moment from "moment";

import RowChart from "../../shared/stats/row_chart";
import ColumnChart from "../../shared/stats/column_chart";
import ColumnsChart from "../../shared/stats/columns_chart";
import BarsChart from "../../shared/stats/bars_chart";
import LineChart from "../../shared/stats/line_chart";

import AssetsMap from "./map";
import DataGroupsGrid from "./grid";

import Helpers from "../../../helpers.js";

export default {
  components: {
    RowChart,
    ColumnChart,
    ColumnsChart,
    BarsChart,
    LineChart,
    AssetsMap,
    DataGroupsGrid
  },

  props: {
    app: Object,
    data: Object,
    googleMapApiKey: String,
    policyUpdate: Boolean
  },

  data: function () {
    return {
      fulcrumApp: {
        id: null,
        name: '',
        statusField: {},
        fields: [],
        dashboardGroups: [],
        lastSync: '',
        updatedAt: ''
      },

      allAssets: [],

      dashboardData: {
        inspectionTypes: [],
        overallStatus: [],
        rejectedAndPriorityPoles: [],
        assets: []
      },

      dataGroups: [],
      selectedDataGroups: [],

      filteredAssets: [],

      isLoaded: false,
      isInitialized: false,
      isOverlayVisible: true,
      isProgressVisible: false,
      progress: 0,
      isSpinnerVisible: true
    }
  },

  setup () {
    return {
      h$: Helpers,
      m$: Moment
    }
  },

  watch: {
    app: {
      deep: true,
      immediate: true,
      handler: function(app) {
        if (app.id) {
          this.fulcrumApp.id = app.id || '';
          this.fulcrumApp.name = app.name || '';
          this.fulcrumApp.statusField = app.statusField || {};
          this.fulcrumApp.fields = app.fields || [];
          this.fulcrumApp.lastSync = app.lastSync || '';
          this.fulcrumApp.updatedAt = app.updatedAt || '';
          this.fulcrumApp.dashboardGroups = app.dashboardGroups || [];
          this.fulcrumApp.dashboardType = app.dashboardType || '';

          this.onAppLoaded(app);
        }
      }
    },
    data: {
      deep: true,
      immediate: true,
      handler: function(data) {        
        setTimeout(() => {
          if (data.length > 0) {
            this.setAppData(data);
          }
        }, 400);
      }
    }
  },

  computed: {
    isDataGroupsEmpty() {
      return this.dataGroups.length === 0;
    } 
  },

  methods: {
    onAppLoaded(app) {},

    setDataGroups() {
      function mergeValues(...objects) {
        const result = {};
        for (const obj of objects) {
          for (const key in obj) {
            result[key] = (result[key] || 0) + obj[key];
          }
        }
        return result;
      }

      function capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
      }

      function calculateDerivedFields(node) {
        const scope = node.scope || {};
        const status = node.status || {};

        // Total count of all assets (with any status) for the selected grouping.
        const originalScope = scope.true || 0;
        const addPoles = scope.false || 0;
        const totalScope = originalScope + addPoles;
        
        // Total count of all inspections (with any status) for the selected grouping.
        const inspections = Object.values(status).reduce((sum, val) => sum + val, 0);

        // Total count of poles with “Not Visited” status ("N", "N-Pad", "NI")
        const notVisited = Object.entries(status)
          .filter(([k]) => ["N", "N-Pad", "NI"].includes(k))
          .reduce((sum, [, val]) => sum + val, 0);

        // Count of poles with “Not Visited” status vs any other status
        const complete = inspections - notVisited;

        // Total Scope - Complete
        const remaining = totalScope - complete;
        //const remaining = totalScope - inspections;

        // Total count of poles with the status having the “-QC” suffix
        const statusesQC = Object.entries(status)
          .filter(([k]) => k.endsWith('QC'))
          .reduce((sum, [, val]) => sum + val, 0);

        return {
          originalScope,
          addPoles,
          totalScope,
          inspections,
          complete,
          remaining,
          statusesQC
        };
      }

      function buildDataGroups(scope, status, approvedRecords, dashboardGroups) {
        const labelMap = Object.fromEntries(
          dashboardGroups.map(d => [d.data_name, d.label])
        );

        const hierarchyOrder = dashboardGroups.map(d => d.data_name);

        const allRecords = [];

        // Step 1: Combine scope and status into allRecords
        for (const item of scope) {
          allRecords.push({
            group: item.group,
            scope: item.values || {},
            status: {}
          });
        }

        for (const item of status) {
          allRecords.push({
            group: item.group,
            scope: {},
            status: item.values || {},
            hasApprovedRecords: false
          });
        }

        for (const item of approvedRecords) {
          allRecords.push({
            group: item.group,
            scope: {},
            status: {},
            approved: item.approved,
            hasApprovedRecords: true
          });
        }

        // Step 2: Normalize missing parent groups AND create virtual entries
        const virtualEntries = [];
        const seenGroups = new Set(); // to prevent duplicates

        function generateGroupKey(group) {
          return hierarchyOrder.map(k => group[k] ?? '').join('>>');
        }

        for (const entry of allRecords) {
          const group = entry.group || {};
          const normalizedGroup = {};
          let lastDefinedIndex = -1;

          // Step 2.1: Find the deepest level that has a defined value
          for (let i = 0; i < hierarchyOrder.length; i++) {
            const key = hierarchyOrder[i];
            if (group[key] != null) {
              lastDefinedIndex = i;
            }
          }

          // Step 2.2: Build full hierarchy up to the deepest level
          for (let i = 0; i <= lastDefinedIndex; i++) {
            const key = hierarchyOrder[i];
            normalizedGroup[key] = group[key] != null ? group[key] : "undefined";

            // Step 2.3: For each intermediate level, create a virtual group if it doesn't exist
            const partialGroup = {};
            for (let j = 0; j <= i; j++) {
              const k = hierarchyOrder[j];
              partialGroup[k] = normalizedGroup[k];
            }

            const gKey = generateGroupKey(partialGroup);
            if (!seenGroups.has(gKey)) {
              seenGroups.add(gKey);

              // Only insert virtual entry if it's not already the original entry
              const isOriginal = JSON.stringify(partialGroup) === JSON.stringify(entry.group);
              if (!isOriginal) {
                virtualEntries.push({
                  group: { ...partialGroup },
                  scope: {},
                  status: {}
                });
              }
            }
          }

          // Update original entry's group
          entry.group = normalizedGroup;
        }

        // Step 2.4: Append virtual entries to allRecords
        allRecords.push(...virtualEntries);

        // Step 3: Build nested structure
        const root = {};

        for (const entry of allRecords) {
          const keys = Object.keys(entry.group);
          let current = root;

          for (let i = 0; i < keys.length; i++) {
            const type = keys[i];
            const name = entry.group[type];

            if (name == null) continue; // skip null/undefined

            if (!current[name]) {
              current[name] = {
                type,
                label: labelMap[type] || capitalize(type),
                name,
                scope: {},
                status: {},
                approved: null,
                hasApprovedRecords: false,
                dataGroups: {}
              };
            }

            // Merge values
            current[name].scope = mergeValues(current[name].scope, entry.scope);
            current[name].status = mergeValues(current[name].status, entry.status);

            if (entry.hasApprovedRecords) {
              current[name].approved = current[name].hasApprovedRecords
                ? current[name].approved && entry.approved
                : entry.approved;
              current[name].hasApprovedRecords = true;
            }

            // Move deeper
            current = current[name].dataGroups;
          }
        }

        // Step 4: Convert nested object to array
        function convertToArray(obj) {
          return Object.values(obj).map(entry => {
            const dataGroups = convertToArray(entry.dataGroups);
            const derived = calculateDerivedFields(entry);

            return {
              type: entry.type,
              label: entry.label,
              name: entry.name,
              scope: entry.scope,
              status: entry.status,
              approved: Boolean(entry.approved),
              dataGroups,
              ...derived
            };
          });
        }

        return convertToArray(root);
      }

      // Get Original Scope (true) vs Add Pole (false) grouped by dashboard groups
      const dataGroupsScope = this.getDashboardDataScope();
      
      // Get Status grouped by dashboard groups
      const dataGroupsStatus = this.getDashboardDataStatus();

      // Get approval state grouped by dashboard groups
      const dataGroupsApproval = this.getDashboardDataApproval();

      // Build data groups
      this.dataGroups = buildDataGroups(dataGroupsScope, dataGroupsStatus, dataGroupsApproval, this.fulcrumApp.dashboardGroups);
    },

    onDataGroupsSelectionChange(selectedDataGroups) {
      this.selectedDataGroups = selectedDataGroups;

      this.filterMapAssets();
    },

    getDashboardDataApproval() {
      const dashboardGroups = this.fulcrumApp.dashboardGroups.map(item => item.data_name);

      const approvalMap = new Map();

      for (const asset of this.allAssets) {
        const group = {};

        // Build group object excluding nulls
        for (const field of dashboardGroups) {
          const val = asset[field];
          if (val != null) {
            group[field] = val;
          }
        }

        const groupKey = JSON.stringify(group);
        const isApproved = asset.approved === true;

        if (!approvalMap.has(groupKey)) {
          approvalMap.set(groupKey, { group, approved: isApproved });
        } else {
          const entry = approvalMap.get(groupKey);
          entry.approved = entry.approved && isApproved;
        }
      }

      return Array.from(approvalMap.values());
    },
  },
}
